import React, { useEffect } from "react";
import { useTitle } from "../provider/TitleProvider";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{title}</h3>
    <div className="space-y-3 text-sm text-gray-600 leading-relaxed">{children}</div>
  </div>
);

const Privacy = () => {
  const { setTitle } = useTitle();

  useEffect(() => {
    setTitle("Privacy Policy");
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm space-y-8">
        <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
          <p>
            This privacy policy (
            <em>
              <strong>Policy</strong>
            </em>
            ) applies to Fintree Holding Group Pty Ltd Trading as Buysooner (ABN 663 384 889) (Buysooner, we, us or
            our), and explains how Buysooner collects, holds, uses and discloses personal information and sensitive
            information.
          </p>
          <p>
            Buysooner is committed to protecting your privacy and this{" "}
            <em>
              <strong>Policy</strong>
            </em>{" "}
            applies to our business, including our websites, online experiences and mobile apps for Apple iOS, Windows,
            or Android mobile devices that link to this{" "}
            <em>
              <strong>Policy</strong>
            </em>{" "}
            (collectively, our "Sites"), and describes how we collect, use and disclose your personal information when
            you visit our Sites or use our products and solutions, or otherwise engage with us (collectively, our
            Services). By accessing or using our Services, you indicate that you have read, understood, and agree to our
            collection, storage, use, and disclosure of your personal information as described in this{" "}
            <em>
              <strong>Policy</strong>
            </em>
            .
          </p>
        </div>

        <div className="border-t border-gray-200" />

        <Section title="1. What personal information do we collect and hold about you and how do we do this?">
          <p>
            The "personal information" (being information that is an opinion, whether true or not and whether recorded
            in a material form or not, about an individual who is identified or reasonably identifiable), that we
            collect about you can vary depending on who you are and how we interact with you, but generally includes:
          </p>
          <p>If you are an applicant:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>your name, contact details, date of birth, residency status and education;</li>
            <li>information about your intended property (the property which our services will relate to);</li>
            <li>
              details about your circumstances, including as to your dependents, borrowing capacity, lenders mortgage
              insurance status, income history, rental properties and job history; and
            </li>
            <li>
              other information required to be collected by us under the Anti-Money Laundering and Counter-Terrorism
              Financing Act 2006 or that will help us to comply with our obligations in relation to that Act.
            </li>
          </ul>
          <p>
            You do not need to provide such information to us but we may not be able to accept your application if the
            information is not supplied.
          </p>
          <p>
            In other more general circumstances (for example, where you are a representative of a business partner or
            supplier, apply for employment or communicate with us via our website or our other channels):
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>your name and contact details;</li>
            <li>
              other details that you provide to us, including information contained in an application for employment or
              queries about our Services;
            </li>
            <li>comments, chat or opinions (including your preferences as to marketing or our Services); and</li>
            <li>
              any relevant billing or account information (including but not limited to bank account details, direct
              debit, credit card details, billing address).
            </li>
          </ul>
          <p>
            We will always try to collect personal information from you directly but in some circumstances we may
            collect your personal information from a third party (for example, where a third party is making the
            application for you on your behalf).
          </p>
          <p>
            "Sensitive information" is personal information which may include, but is not limited to, health information
            and information about your racial or ethnic origins or political or religious beliefs. We do not routinely
            collect sensitive information.
          </p>
        </Section>

        <Section title="2. How do we hold, use and disclose personal information?">
          <p>We may use your information in the instances and for the primary purposes described below.</p>
          <p>
            <strong>To fulfill your requests and provide our Services to you.</strong> We will use personal information
            to provide the Services to you, or respond to your customer service requests or feedback, including to
            process your application, purchasing the exposure, managing the investment and complying with relevant laws.
          </p>
          <p>
            <strong>For marketing purposes.</strong> We may send you communications about new features, updates,
            products and special offers. We may also use your information to serve you ads about products and special
            offers about our products or other products or services we think you might find interesting.
          </p>
          <p>
            <strong>To communicate with you.</strong> For example, we may communicate with you to handle enquiries and
            complaints and in relation to any application you make. We may communicate with you by email, postal mail,
            telephone, text message, or other means.
          </p>
          <p>
            <strong>To monitor, improve and develop our products and services.</strong> We may use your information to
            understand you and our other customers, and improve our Services and tailor our product offerings
            accordingly.
          </p>
          <p>
            <strong>To protect the security and integrity of our business.</strong> We may use information to protect
            our company, our affiliates, our customers, our franchisees and our websites. We may also use information in
            order to comply with laws, regulations, court orders, or other legal obligations.
          </p>
        </Section>

        <Section title="3. Cookies">
          <p>
            When you visit a website hosted by us, we may set a 'cookie' on your device (this is a small piece of system
            information stored on your device) so when you next visit, it links to your system and allows us to
            recognise that you have returned and remember your preferences. If you do not wish for us to use a cookie,
            you can set your browser so it will not accept them. However, if you use your browser settings to block all
            cookies (including essential cookies) you may not be able to access all or part of our website.
          </p>
        </Section>

        <Section title="4. How we share your information">
          <p>We may share, sell or disclose your information in the following instances:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Our related entities:</strong> We may share personal information with our affiliates.
            </li>
            <li>
              <strong>Third parties for Services:</strong> We may share your personal information to share registries,
              custodians and certain software providers related to the operational management and settlement of
              interests in deferred purchase agreements.
            </li>
            <li>
              <strong>With your consent:</strong> We may share information about you with third parties whenever you
              consent to or direct such sharing.
            </li>
            <li>
              <strong>Business transfers:</strong> We may share your information in connection with a substantial
              corporate transaction.
            </li>
            <li>
              <strong>Legal purposes:</strong> We may disclose your information to third parties in order to comply with
              laws, regulations, legal processes, or governmental requests.
            </li>
          </ul>
        </Section>

        <Section title="5. Your choices and control over your information">
          <p>
            <strong>Your right not to provide personal information:</strong> You do not have to provide personal
            information to us, however, if you do not then it may affect our ability to provide our Services to you.
          </p>
          <p>
            <strong>Profile and settings:</strong> You may update your account information and adjust your account
            settings by logging into your account or contacting us.
          </p>
          <p>
            <strong>Email preferences:</strong> You can stop receiving promotional email communications from us by
            clicking on the "unsubscribe link" provided in such communications.
          </p>
          <p>
            <strong>Anonymity:</strong> You have the option of not identifying yourself, or to use a pseudonym when
            dealing with us. However, in some cases it will not be practicable to deal with you anonymously.
          </p>
        </Section>

        <Section title="6. How we store and protect your information">
          <p>
            Your personal information may be stored and processed in Australia or any other country in which we or our
            affiliates or service providers maintain facilities.
          </p>
          <p>
            We will retain your personal information as long as reasonably necessary to maintain the Services, to meet
            legal and accounting obligations, and for the other purposes described in this Policy.
          </p>
          <p>
            Security of your information is very important to us, and we have put in place safeguards to preserve the
            integrity and security of information we collect and share with our service providers.
          </p>
        </Section>

        <Section title="7. Transferring personal information overseas">
          <p>
            Your personal information may be disclosed to employees, representatives, or other third parties operating
            outside of Australia who work for or are engaged by us or our affiliates in other countries. In disclosing
            your personal information overseas, we will take such steps as are reasonable in the circumstances to
            require that overseas recipients protect your personal information in accordance with the Australian Privacy
            Principles.
          </p>
        </Section>

        <Section title="8. Links to other websites and services">
          <p>
            The Services may contain links to and from third party websites. If you follow a link to any of these
            websites, please note that these websites have their own privacy policies and that we do not accept any
            responsibility or liability resulting from you following a link to these websites.
          </p>
        </Section>

        <Section title="9. Access and correction of your personal information">
          <p>
            You may, at any time, request access to and correction of any personal information we hold about you by
            contacting us. If you make an access request, we may ask you to verify your identity and put your request in
            writing for security reasons. We may charge a reasonable administration fee to cover the costs of meeting
            your request.
          </p>
        </Section>

        <Section title="10. How to contact us">
          <p>
            If you have any questions or complaints about how we handle your personal information, please contact us at:
          </p>
          <p className="font-medium text-gray-900">The Privacy Officer, Buysooner — info@buysooner.com</p>
          <p>
            If you are not satisfied with our response, you also have the right to contact the Office of the Australian
            Information Commissioner.
          </p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>
            We may modify or update this Policy from time to time to reflect the changes in our business and practices,
            so you should review this Policy periodically. If you object to any changes, you may close your account
            associated with the Service.
          </p>
        </Section>

        <div className="border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-400">Last modified: 14 June 2024</p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
